import { useCallback, useRef, useState } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVertTwoTone';
import { Drawer, Popover, useMediaQuery, useTheme } from '@mui/material';
import { gridRowIdSelector, gridRowSelectionIdsSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid-pro';
import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import ActionItemList from './ActionItemList';

import styles from '../../RenderCell.module.css';

interface RowActionsProps {
	row?: DispatchLeg;
}

/**
 * Cell containing the action menu and actions available for the dispatch data grid. In mobile this component is used at the top of the grid.
 * The first divided section of actions are applicable to the dispatch board data grid.
 * The second divided section of actions will open a modal or navigate off the board.
 * The third divided section of actions will immediately make a data change.
 */
export default function RowActions(props: RowActionsProps) {
	const cellRef = useRef<HTMLDivElement | null>(null);
	const [isOpen, setIsOpen] = useState(false);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const gridApiRef = useGridApiContext();
	const selectedRowIds = useGridSelector(gridApiRef, gridRowSelectionIdsSelector);

	const onMenuClick = useCallback(() => {
		// In Desktop we should have a row prop, but in mobile we will not because the row action menu is outside the row
		// Use the grid's own row id (not legId) so cancelled loads, which have no leg, still select correctly.
		const rowId = props.row ? gridRowIdSelector(gridApiRef, props.row) : undefined;
		if (rowId !== undefined && !selectedRowIds.has(rowId)) {
			gridApiRef.current.setRowSelectionModel({
				type: 'include',
				ids: new Set([...selectedRowIds.keys(), rowId])
			});
			setIsOpen(true);
		} else if (selectedRowIds.size > 0) {
			setIsOpen(true);
		}
	}, [gridApiRef, selectedRowIds, props.row]);

	/**
	 * Drawer/Popover close event, preventing the menu from closing in a default manner
	 */
	function onClose(_: object, reason?: 'backdropClick' | 'escapeKeyDown') {
		if (reason !== 'backdropClick') {
			setIsOpen(true);
		} else {
			setIsOpen(false);
		}
	}

	/**
	 * Callback to allow the nested components to close the menu manually when needed
	 */
	function subMenuClose() {
		setIsOpen(false);
	}

	// Desktop renders a per-row menu trigger; a load with no leg (cancelled, closed/purged, or deleted) has nothing to act on.
	if (props.row && !props.row.legId) {
		return <div className={styles['row-menu-container']} />;
	}

	if (isMobile) {
		return (
			<>
				<MoreVertIcon
					className={`row-menu-btn ${styles['row-menu-btn']} ${selectedRowIds.size > 0 ? styles.active : ''}`}
					onClick={onMenuClick}
				/>
				<Drawer
					anchor='bottom'
					open={isOpen}
					onClose={onClose}
					onClick={() => {
						setIsOpen(false);
					}}
				>
					<ActionItemList onClose={subMenuClose} />
				</Drawer>
			</>
		);
	}

	return (
		<div
			className={styles['row-menu-container']}
			ref={cellRef}
		>
			<MoreVertIcon
				className={`row-menu-btn ${styles['row-menu-btn']} ${selectedRowIds.size > 0 ? styles.active : ''}`}
				onClick={onMenuClick}
			/>
			<Popover
				open={isOpen}
				anchorEl={cellRef.current ?? null}
				transformOrigin={{
					horizontal: 'left',
					vertical: 'top'
				}}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left'
				}}
				onClose={onClose}
				className={styles['row-menu']}
				slotProps={{
					paper: {
						className: styles['row-menu-paper'],
						onClick: () => {
							setIsOpen(false);
						}
					}
				}}
				transitionDuration={100}
			>
				<ActionItemList onClose={subMenuClose} />
			</Popover>
		</div>
	);
}
